import React, { useState, useEffect } from 'react';
import { Check, Star, Sparkles, Heart, Infinity, Crown, Zap, Clock, Calendar, Phone, MessageCircle, Users, BarChart3, Target, Loader2, ArrowRight } from 'lucide-react';
import { ShimmerButton } from './ShimmerButton';
import { getUserSubscription } from '../lib/stripe';
import { products, getProductByPriceId } from '../stripe-config';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useRevenueCat } from '../contexts/RevenueCatContext';
import { toast } from 'react-hot-toast';

interface CommitmentTier {
  id: string;
  nameKey: string;
  price: number;
  period: string;
  descriptionKey: string;
  featuresKeys: string[];
  manifestationFrequencyKey: string;
  callLimitKey: string;
  icon: React.ComponentType<any>;
  color: string;
  gradient: string;
  popular?: boolean;
  unlimited?: boolean;
  purchaseUrl?: string;
}

export const Commitment: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [userCommitmentTier, setUserCommitmentTier] = useState<string>('seeker');

  const commitmentTiers: CommitmentTier[] = [
    {
      id: 'seeker',
      nameKey: 'commitment.seekerTier',
      price: 0,
      period: 'forever',
      descriptionKey: 'commitment.seekerDescription',
      featuresKeys: [
        'commitment.seekerFeature1',
        'commitment.seekerFeature2',
        'commitment.seekerFeature3',
        'commitment.seekerFeature4'
      ],
      manifestationFrequencyKey: 'commitment.seekerFrequency',
      callLimitKey: 'commitment.seekerCallLimit',
      icon: Star,
      color: 'from-gray-500 to-gray-600',
      gradient: 'from-gray-50 to-gray-100'
    },
    {
      id: 'awakening',
      nameKey: 'commitment.awakeningTier',
      price: 11.11,
      period: 'month',
      descriptionKey: 'commitment.awakeningDescription',
      featuresKeys: [
        'commitment.awakeningFeature1',
        'commitment.awakeningFeature2',
        'commitment.awakeningFeature3',
        'commitment.awakeningFeature4',
        'commitment.awakeningFeature5',
        'commitment.awakeningFeature6'
      ],
      manifestationFrequencyKey: 'commitment.awakeningFrequency',
      callLimitKey: 'commitment.awakeningCallLimit',
      icon: Sparkles,
      color: 'from-purple-500 to-purple-600',
      gradient: 'from-purple-50 to-purple-100',
      purchaseUrl: 'https://pay.rev.cat/hixpaomfsqiizlxx/'
    },
    {
      id: 'aligned',
      nameKey: 'commitment.alignedTier',
      price: 22.22,
      period: 'month',
      descriptionKey: 'commitment.alignedDescription',
      featuresKeys: [
        'commitment.alignedFeature1',
        'commitment.alignedFeature2',
        'commitment.alignedFeature3',
        'commitment.alignedFeature4',
        'commitment.alignedFeature5'
      ],
      manifestationFrequencyKey: 'commitment.alignedFrequency',
      callLimitKey: 'commitment.alignedCallLimit',
      icon: Heart,
      color: 'from-pink-500 to-rose-600',
      gradient: 'from-pink-50 to-rose-100',
      popular: true,
      purchaseUrl: 'https://pay.rev.cat/wkakexlkecdwkvvb/'
    },
    {
      id: 'abundant',
      nameKey: 'commitment.abundantTier',
      price: 88.88,
      period: 'month',
      descriptionKey: 'commitment.abundantDescription',
      featuresKeys: [
        'commitment.abundantFeature1',
        'commitment.abundantFeature2',
        'commitment.abundantFeature3',
        'commitment.abundantFeature4',
        'commitment.abundantFeature5',
        'commitment.abundantFeature6'
      ],
      manifestationFrequencyKey: 'commitment.abundantFrequency',
      callLimitKey: 'commitment.abundantCallLimit',
      icon: Crown,
      color: 'from-amber-500 to-yellow-600',
      gradient: 'from-amber-50 to-yellow-100',
      unlimited: true,
      purchaseUrl: 'https://pay.rev.cat/pitkauzffskwzaoh/'
    }
  ];

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const subscription = await getUserSubscription();
        setCurrentSubscription(subscription);
        
        // Get user's commitment tier from profile
        const userWithProfile = user as any; // Type assertion since getCurrentUser adds profile
        if (userWithProfile.profile?.commitment_tier) {
          setUserCommitmentTier(userWithProfile.profile.commitment_tier);
        }
      } catch (err: any) {
        console.error('Error fetching subscription:', err);
        setError(t('commitment.errorLoadingSubscription'));
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user, t]);

  const handleSubscribe = async (tier: CommitmentTier) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (tier.price === 0) {
      setIsProcessing(true);
      setSelectedTier(tier.id);
      
      // For free tier, show success message with animation
      setTimeout(() => {
        setIsProcessing(false);
        setSelectedTier(null);
        
        // Show success toast instead of alert
        toast.success(t('commitment.welcomeSeekerMessage'), {
          duration: 5000,
          icon: '✨',
          style: {
            borderRadius: '10px',
            background: '#f0f9ff',
            color: '#0369a1',
            border: '1px solid #bae6fd',
          },
        });
        
        // Navigate to home page after a short delay
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }, 1500);
      
      return;
    }

    if (!tier.purchaseUrl) {
      toast.error(t('commitment.tierNotAvailableMessage'), {
        icon: '⚠️',
      });
      return;
    }

    setIsProcessing(true);
    setSelectedTier(tier.id);

    try {
      // Construct RevenueCat purchase link with user ID
      // RevenueCat requires the App User ID at the end of the URL when Redemption Links are disabled
      const userIdForRevenueCat = user.id; // Use Supabase user ID as the App User ID
      const purchaseUrl = `${tier.purchaseUrl}${userIdForRevenueCat}`;
      
      console.log(`Redirecting to RevenueCat purchase URL: ${purchaseUrl}`);
      
      // Redirect to RevenueCat purchase link with user ID
      window.location.href = purchaseUrl;
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error(`${t('commitment.subscriptionError')}: ${error.message}`);
      setIsProcessing(false);
      setSelectedTier(null);
    }
  };

  const getButtonTextForNonLoggedUser = (tier: CommitmentTier, index: number) => {
    if (index === 0) {
      return t('commitment.currentPlan');
    }
    return t('commitment.levelUp');
  };

  const formatPrice = (price: number) => {
    if (price === 0) return t('commitment.free');
    return `$${price.toFixed(2)}`;
  };

  const getCurrentTierName = () => {
    if (!currentSubscription) {
      // If no subscription, use the user profile tier
      const tierKey = `commitment.${userCommitmentTier}Tier`;
      return t(tierKey);
    }
    
    const product = getProductByPriceId(currentSubscription.price_id);
    return product ? product.name : t('commitment.unknown');
  };

  const isCurrentTier = (tier: CommitmentTier) => {
    if (!currentSubscription) {
      // If no subscription, check against user profile tier
      return tier.id === userCommitmentTier;
    }
    
    const product = getProductByPriceId(currentSubscription.price_id);
    if (!product) return false;
    
    return product.name.toLowerCase().includes(tier.id);
  };

  const canUpgrade = (tier: CommitmentTier) => {
    if (!currentSubscription) {
      // If no subscription, check against user profile tier
      const tierLevels: Record<string, number> = {
        'seeker': 1,
        'awakening': 2,
        'aligned': 3,
        'abundant': 4
      };
      
      const currentLevel = tierLevels[userCommitmentTier] || 1;
      const targetLevel = tierLevels[tier.id] || 1;
      
      return targetLevel > currentLevel;
    }
    
    const currentTierIndex = commitmentTiers.findIndex(t => isCurrentTier(t));
    const targetTierIndex = commitmentTiers.findIndex(t => t.id === tier.id);
    
    return targetTierIndex > currentTierIndex;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-6 py-2 mb-6 border border-purple-200">
            <Infinity size={20} className="text-purple-600" />
            <span className="text-purple-700 font-medium">{t('commitment.commitmentLevels')}</span>
          </div>
          
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            {t('commitment.title')}
          </h1>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            {t('commitment.subtitle')}
          </p>
          
          {/* Current Subscription Status */}
          {user && (
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 max-w-md mx-auto border border-purple-200 mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('commitment.currentCommitmentLevel')}</h3>
              <div className="flex items-center justify-center space-x-3">
                {userCommitmentTier === 'seeker' && <Star size={20} className="text-gray-600" />}
                {userCommitmentTier === 'awakening' && <Sparkles size={20} className="text-purple-600" />}
                {userCommitmentTier === 'aligned' && <Heart size={20} className="text-pink-600" />}
                {userCommitmentTier === 'abundant' && <Crown size={20} className="text-amber-600" />}
                <p className="text-xl font-semibold text-purple-700">{getCurrentTierName()}</p>
              </div>
            </div>
          )}

          {!user && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-amber-800 text-sm">
                <Link to="/login" className="font-medium underline">{t('auth.login')}</Link> {t('commitment.viewCurrentLevel')}
              </p>
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {commitmentTiers.map((tier, index) => {
            const Icon = tier.icon;
            const isSelected = selectedTier === tier.id;
            const isProcessingThis = isProcessing && isSelected;
            const isCurrent = isCurrentTier(tier);
            const canUpgradeToTier = canUpgrade(tier);
            
            return (
              <div
                key={tier.id}
                className={`relative bg-white rounded-2xl shadow-xl border-2 transition-all duration-300 transform hover:scale-105 flex flex-col h-full ${
                  tier.popular 
                    ? 'border-pink-300 ring-4 ring-pink-100' 
                    : isCurrent
                    ? 'border-green-300 ring-4 ring-green-100'
                    : 'border-gray-200 hover:border-purple-300'
                } ${isSelected ? 'ring-4 ring-purple-200' : ''}`}
              >
                {/* Popular Badge */}
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                      {t('commitment.mostPopular')}
                    </div>
                  </div>
                )}

                {/* Unlimited Badge */}
                {tier.unlimited && (
                  <div className="absolute -top-4 right-4 z-10">
                    <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white p-2 rounded-full shadow-lg">
                      <Infinity size={16} />
                    </div>
                  </div>
                )}

                {/* Card Header */}
                <div className={`bg-gradient-to-r ${tier.gradient} p-6 rounded-t-2xl border-b border-gray-100`}>
                  <div className="text-center">
                    <div className={`w-16 h-16 bg-gradient-to-r ${tier.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <Icon size={24} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{t(tier.nameKey)}</h3>
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <span className="text-3xl font-bold text-gray-900">{formatPrice(tier.price)}</span>
                      {tier.price > 0 && (
                        <span className="text-gray-600">/{t(`commitment.${tier.period}`)}</span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">{t(tier.descriptionKey)}</p>
                  </div>
                </div>

                {/* Card Content - Flex grow to push button to bottom */}
                <div className="p-6 flex flex-col flex-grow">
                  {/* Key Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <Calendar size={14} className="text-purple-600" />
                        <span className="text-xs font-medium text-gray-600">{t('commitment.manifestationCards')}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{t(tier.manifestationFrequencyKey)}</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <Phone size={14} className="text-purple-600" />
                        <span className="text-xs font-medium text-gray-600">Call Manivo</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{t(tier.callLimitKey)}</span>
                    </div>
                  </div>

                  {/* Features - Flex grow to take available space */}
                  <div className="space-y-3 mb-8 flex-grow">
                    {tier.featuresKeys.map((featureKey, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-3">
                        <div className={`w-5 h-5 bg-gradient-to-r ${tier.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Check size={12} className="text-white" />
                        </div>
                        <span className="text-gray-700 text-sm leading-relaxed">{t(featureKey)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Current Plan Indicator - Positioned at bottom */}
                  {isCurrent && (
                    <div className="mb-4">
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg text-center font-semibold text-sm">
                        {t('commitment.currentPlan')}
                      </div>
                    </div>
                  )}

                  {/* CTA Button - Always at bottom */}
                  <div className="mt-auto">
                    {!isCurrent && (
                      <ShimmerButton
                        onClick={() => handleSubscribe(tier)}
                        disabled={isProcessingThis}
                        background={`linear-gradient(135deg, rgb(147 51 234), rgb(219 39 119))`}
                        shimmerDuration="2.5s"
                        className="w-full font-semibold"
                      >
                        {isProcessingThis ? (
                          <div className="flex items-center space-x-2">
                            <Loader2 size={16} className="animate-spin" />
                            <span>{t('commitment.processing')}</span>
                          </div>
                        ) : !user ? (
                          getButtonTextForNonLoggedUser(tier, index)
                        ) : tier.price === 0 ? (
                          t('commitment.startFreeJourney')
                        ) : (
                          `${t('commitment.commit')} ($${tier.price})`
                        )}
                      </ShimmerButton>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Commitment Philosophy */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-12 border border-purple-100">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('commitment.commitmentPhilosophy')}</h2>
            <p className="text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('commitment.philosophyText')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap size={24} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('commitment.energyAlignment')}</h3>
              <p className="text-gray-600 text-sm">
                {t('commitment.energyAlignmentDesc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart size={24} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('commitment.sacredExchange')}</h3>
              <p className="text-gray-600 text-sm">
                {t('commitment.sacredExchangeDesc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown size={24} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('commitment.abundanceMindset')}</h3>
              <p className="text-gray-600 text-sm">
                {t('commitment.abundanceMindsetDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Success Modal for Seeker Tier */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={24} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('commitment.welcomeSeekerMessage')}</h2>
              <p className="text-gray-600 mb-6">
                Your journey begins now. Explore the app to start manifesting your dreams.
              </p>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/');
                }}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
              >
                Begin Your Journey
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};